/**
 * packages/shared/src/inventory/serial-placeholder.ts
 *
 * SIL-1647. DMI serial fields are filled by the board vendor's BIOS, and a
 * large share of real servers ship placeholder text instead of a serial
 * ("To Be Filled By O.E.M.", "Default string", "0"). Those strings are
 * identical across thousands of unrelated machines, so treating one as an
 * identity would collide unrelated customers' chassis on the exchange-wide
 * (manufacturer, normalized serial) index (799) - a false foreign-org
 * refusal at best, a wipe certificate attached to the wrong asset at worst.
 * A placeholder is therefore "no serial", never a weak serial.
 */

export type ChassisSerialField = 'product_serial' | 'chassis_serial' | 'board_serial';

/** Below this length a "serial" is too short to be unique across a fleet. */
export const MIN_SERIAL_LENGTH = 4;

// Compared after lowercasing and stripping every non-alphanumeric, so
// 'To Be Filled By O.E.M' (no dot), 'N/A' and 'n.a.' collapse to one form.
// ponytail: fixed list from the BIOS strings seen in the field; extend it when a new placeholder shows up in serial_collision.
const PLACEHOLDERS = new Set(
  [
    'to be filled by o.e.m.',
    'to be filled by oem',
    'default string',
    'system serial number',
    'chassis serial number',
    'base board serial number',
    'serial number',
    'not specified',
    'not applicable',
    'not available',
    'none',
    'n/a',
    'na',
    'unknown',
    'invalid',
    'oem',
    'empty',
    '123456789',
    '0123456789',
    '1234567890',
  ].map((p) => p.toLowerCase().replace(/[^a-z0-9]/g, '')),
);

// SIL-2490: strings that stand in for a model / vendor / part number rather than name one. Same compare form as PLACEHOLDERS.
const EXTRA_VALUE_PLACEHOLDERS = new Set(
  [
    'generic', 'undefined', 'null', 'nodimm', 'no dimm', 'no module installed', 'unspecified', 'not provided', 'tbd', 'test',
    'to be filled', 'unknown vendor', 'unknown model', 'manufacturer', 'model', 'part number', 'vendor', 'default',
  ].map((p) => p.toLowerCase().replace(/[^a-z0-9]/g, '')),
);

/**
 * SIL-2490. True when a vendor / model / part-number string is blank or a firmware placeholder, so it can never be part of a
 * hardware fingerprint (two unrelated boxes reading "To Be Filled By O.E.M." must not share a key). The serial placeholder
 * list plus a few model-shaped ones; unlike isUsableSerial there is no minimum length (a model like "L4" is real).
 */
export function isPlaceholderValue(raw: string | null | undefined): boolean {
  if (raw == null) return true;
  const alnum = raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (alnum.length === 0) return true;
  if (PLACEHOLDERS.has(alnum) || EXTRA_VALUE_PLACEHOLDERS.has(alnum)) return true;
  return alnum.length >= 4 && /^(.)\1*$/.test(alnum);
}

export function isUsableSerial(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  const trimmed = raw.trim();
  if (trimmed.length < MIN_SERIAL_LENGTH) return false;
  const alnum = trimmed.toLowerCase().replace(/[^a-z0-9]/g, '');
  // Nothing but punctuation: '........'.
  if (alnum.length === 0) return false;
  if (PLACEHOLDERS.has(alnum)) return false;
  // One character repeated, separators ignored: '0000000000', 'FFFFFFFF',
  // 'xxxxxxxx', '0000-0000', '00:00:00'.
  if (/^(.)\1*$/.test(alnum)) return false;
  return true;
}

/**
 * The chassis identity a bench reading uses: the first usable value in the
 * order product_serial (SMBIOS type 1, what the vendor prints on the label),
 * chassis_serial (type 3), board_serial (type 2). The field is returned so
 * the caller can record which one matched.
 */
export function pickChassisSerial(c: {
  productSerial?: string | null;
  chassisSerial?: string | null;
  boardSerial?: string | null;
}): { serial: string; field: ChassisSerialField } | null {
  const ordered: Array<[ChassisSerialField, string | null | undefined]> = [
    ['product_serial', c.productSerial],
    ['chassis_serial', c.chassisSerial],
    ['board_serial', c.boardSerial],
  ];
  for (const [field, value] of ordered) {
    if (isUsableSerial(value)) return { serial: value!.trim(), field };
  }
  return null;
}
